import React, { useState } from 'react';

import { BkFltCrd, SpinnerLoader } from '../../common-elements.styles';
import * as S from './cancellation-and-reschedule-penality.styles';
import ArrowHdIcon from '../../../../icons/ArrowDown';

import CancellationReschedulePolicyOverlay from './cancellation-and-reschedule-policy-overlay';
import CancellationAndRescheduleTable from './cancellation-and-reschedule-table';

import { ICON_BASE_URL, CANCELLATION_ID, DATE_CHANGE_ID } from '@stencil/flights-constants';

const CancellationRulesList = ({
  penaltyData,
  id,
  setShowPoliciesOverlay,
  logCancellationAndRescheduleDetails,
  viewPolicyCta
}) => {
  const {
    PenaltyType,
    table_header: tableHeading,
    penalties,
    void_cancellation: voidCancellation
  } = penaltyData;

  const viewPolicyClickHandler = () => {
    const action_name = `view_more_${
      id === CANCELLATION_ID ? 'cancellation' : 'reschedule'
    }_clicked`;
    logCancellationAndRescheduleDetails(action_name, viewPolicyCta?.a);
    setShowPoliciesOverlay(id);
  };

  return (
    <S.CancellationPenalityBlock>
      <div className="marginB15">
        <span className="f500 font14 marginR10" id="cancellation_charges">
          {PenaltyType}
        </span>
        <span
          className="f500 blueCnt curPointFlt"
          id="cancellation_policy"
          onClick={viewPolicyClickHandler}
        >
          View Policy
        </span>
      </div>
      <CancellationAndRescheduleTable
        tableHeading={tableHeading}
        penalties={penalties}
        voidCancellation={voidCancellation}
      />
    </S.CancellationPenalityBlock>
  );
};

function CancellationDetails({
  cancellationRules,
  dateChangeRules,
  isFirstTab = false,
  setShowPoliciesOverlay,
  logCancellationAndRescheduleDetails,
  cancellationViewPolicyCta,
  rescheduleViewPolicyCta
}) {
  const carriers = cancellationRules?.icons || dateChangeRules?.icons;
  const title = cancellationRules?.title || dateChangeRules?.title;

  const {
    refund_status: refundStatus = '',
    refund_status_font_colour = '',
    refund_status_bg_colour = []
  } = cancellationRules || {};

  const [expandSection, setExpandSection] = useState(isFirstTab);

  return (
    <S.TicketDetailWrap cnclPolicy>
      <S.CnclPolicySubHd className="dF justifyBetween">
        <span className="dF alignItemsCenter">
          {carriers.map((carrier, index) => (
            <img
              key={index}
              src={`${ICON_BASE_URL}v2/app-img/${carrier}.webp`}
              width="24"
              height="24"
              className="marginR8"
            />
          ))}
          <span
            className="font14"
            id="cancellation_sector"
            dangerouslySetInnerHTML={{ __html: title }}
          ></span>
        </span>
        <span className="dF" onClick={() => {}}>
          {refundStatus && (
            <S.RefundTag
              id="cancellation_refundtag"
              className="font14 f500"
              bgColor={refund_status_bg_colour}
              fontColor={refund_status_font_colour}
            >
              {refundStatus}
            </S.RefundTag>
          )}
          <S.ArrowWrap
            id="cancellation_close"
            className="marginL10 curPointFlt"
            onClick={() => setExpandSection(!expandSection)}
          >
            <ArrowHdIcon
              width="1rem"
              height="1rem"
              className={`${expandSection ? 'downArw' : ''}`}
            />
          </S.ArrowWrap>
        </span>
      </S.CnclPolicySubHd>

      {expandSection && (
        <S.CancellationWrap className="dF justifyBetween">
          {cancellationRules && (
            <CancellationRulesList
              penaltyData={cancellationRules}
              id={CANCELLATION_ID}
              setShowPoliciesOverlay={setShowPoliciesOverlay}
              logCancellationAndRescheduleDetails={logCancellationAndRescheduleDetails}
              viewPolicyCta={cancellationViewPolicyCta}
            />
          )}
          {dateChangeRules && (
            <CancellationRulesList
              penaltyData={dateChangeRules}
              id={DATE_CHANGE_ID}
              setShowPoliciesOverlay={setShowPoliciesOverlay}
              logCancellationAndRescheduleDetails={logCancellationAndRescheduleDetails}
              viewPolicyCta={rescheduleViewPolicyCta}
            />
          )}
        </S.CancellationWrap>
      )}
    </S.TicketDetailWrap>
  );
}

const CancellationAndReschedulePenality = ({
  error,
  fareRulesData,
  fareRulesSectorsOrder,
  logCancellationAndRescheduleDetails,
  isSRP = false,
  showHeader = true,
  showLoader = false
}) => {
  const [showPoliciesOverlay, setShowPoliciesOverlay] = useState('');

  if (error && !fareRulesData && showLoader) {
    return (
      <div className="dF padT20 padB20 alignItemsCenter justifyCenter">
        <p>{error}</p>
      </div>
    );
  }

  if (showLoader && (!fareRulesData || !fareRulesSectorsOrder?.length)) {
    return (
      <div className="padT20">
        <SpinnerLoader />
      </div>
    );
  }

  if (!showLoader && (!fareRulesData || !fareRulesSectorsOrder?.length)) {
    return null;
  }

  const { listing: fareRulesListing, tabs: fareRulesPolicies, note = '' } = fareRulesData;
  const { cancellation, date_change: dateChange } = fareRulesListing;
  const cancellationViewPolicyCta = cancellation?.view_policy_cta;
  const rescheduleViewPolicyCta = dateChange?.view_policy_cta;

  const getPenalityRules = (penalityList, sector) => {
    return penalityList?.find(penality => penality.sector === sector);
  };

  return (
    <>
      <BkFltCrd isSRP={isSRP}>
        {(showHeader && (
          <S.Header>
            <div className="dF justifyBetween">
              <S.ListBar />
              <div className="f600 font22" id="cancellation_header">
                Cancellation & Date Change Policy
              </div>
            </div>
          </S.Header>
        )) ||
          null}
        <S.TicketDetailBody bottomSpc>
          {fareRulesSectorsOrder.map((sector, index) => {
            const cancellationRules = getPenalityRules(cancellation?.penalty_list, sector);
            const dateChangeRules = getPenalityRules(dateChange?.penalty_list, sector);
            return (
              <CancellationDetails
                cancellationRules={cancellationRules}
                dateChangeRules={dateChangeRules}
                key={sector}
                setShowPoliciesOverlay={setShowPoliciesOverlay}
                isFirstTab={index === 0}
                logCancellationAndRescheduleDetails={logCancellationAndRescheduleDetails}
                cancellationViewPolicyCta={cancellationViewPolicyCta}
                rescheduleViewPolicyCta={rescheduleViewPolicyCta}
              />
            );
          })}
          {(note && (
            <div
              className="marginT15 greyCnt font14"
              dangerouslySetInnerHTML={{ __html: note }}
            ></div>
          )) ||
            null}
        </S.TicketDetailBody>
      </BkFltCrd>
      {showPoliciesOverlay && (
        <CancellationReschedulePolicyOverlay
          activePolicySection={showPoliciesOverlay}
          setShowPoliciesOverlay={setShowPoliciesOverlay}
          fareRulesPolicies={fareRulesPolicies}
          logCancellationAndRescheduleDetails={logCancellationAndRescheduleDetails}
        />
      )}
    </>
  );
};

export default CancellationAndReschedulePenality;
